/**
 * @fileoverview Signature widget — Mode C event signature capture.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Phase 3 renders a presentational capture button + thumbnail. Phase 4
 *   wires the real `<SignaturePad>` from `@firesquirrel/attachments`. The
 *   captured `AttachmentRefLike` is stored as the field value but extracted
 *   at completion time into `events[].signatures[]` per D-0.16.
 *
 * @component SignatureField
 * Dependencies: react, ./FieldWrapper, ./widgetTypes, @firesquirrel/core-domain
 * Architecture: Keep behavior within the existing package and local-first boundaries.
 * Notes: Preserve existing behavior and synchronization invariants.
 */
import { useState } from 'react';
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';
import { inferSignatureRole } from '@firesquirrel/core-domain';
import { useAttachmentPicker, useInspectionAuditEvent, useIsOffline } from '../contracts/rendererContext';
import { AttachmentThumb } from './AttachmentThumb';

/**
 * @component SignatureField
 * Purpose: Capture or preview the event signature for a form field.
 * Props: Canonical signature attachment value and field/widget policy.
 * Behavior: Uses the host attachment picker and thumbnail resolver.
 * Data Source: Host-injected attachment services.
 * Constraints: Capture is disabled when read-only or no picker is available.
 * Performance: Resolves only the saved signature thumbnail.
 * @errorHandling SignatureField
 * Strategy: Use the implementation’s existing catch, return, and propagation behavior.
 * User Impact: Callers can surface operation failures without silently treating them as success.
 * Logging: Retain existing operation logging and avoid sensitive values.
 * @logging SignatureField
 * Events: Record the operation outcome and actionable failures.
 * Level: Use the existing operation-appropriate info, warn, or error level.
 * Correlation: Correlate with the case, event, or operation identifier when available.
 */
export function SignatureField(props: BaseWidgetProps<AttachmentRefLike | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, bulkPilotMode, errorMessage, requiredUnmet } = props;
  const picker = useAttachmentPicker();
  const isOffline = useIsOffline();
  const emitInspectionAuditEvent = useInspectionAuditEvent();
  const disabledByPolicy = !!resolved?.disabled || !!bulkPilotMode || !picker;
  const [captureError, setCaptureError] = useState<string | null>(null);

  // D-0.16 signature role inference — explicit extras.signatureRole wins;
  // heuristic fallback derived from label; 'other' when unclassifiable.
  const sigRole = inferSignatureRole({
    label: field.label,
    extras: field.extras as Record<string, unknown> | undefined,
  });

  async function capture() {
    if (disabledByPolicy || !picker) return;
    setCaptureError(null);
    try {
      const ref = await picker.captureSignature({
        parentType: 'eventSignature',
        parentId: field.id,
        kind: 'signature',
      });
      onChange(ref);
      emitInspectionAuditEvent({
        kind: 'signature_captured',
        fieldId: field.id,
        fieldLabel: field.label,
        summaryContext: sigRole.roleLabel,
      }, disabledByPolicy);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // User-cancelled is not an error — silently ignore.
      if (msg === 'SIGNATURE_CANCELLED') return;
      console.error('[SignatureField] capture failed:', msg);
      setCaptureError(
        msg === 'SIGNATURE_STAGING_FAILED' || msg === 'SIGNATURE_NOT_WIRED'
          ? 'Failed to save signature. Please try again.'
          : 'Signature capture failed. Please try again.',
      );
    }
  }

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="signature"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={errorMessage}
      requiredUnmet={requiredUnmet} requiredImmediate={props.requiredImmediate}
    >
      <div data-signature-role={sigRole.role} data-signature-role-label={sigRole.roleLabel ?? ''} className="flex flex-col gap-1">
        {isOffline && (
          <span data-pill="offline" role="status" className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
            offline
          </span>
        )}
        {value ? (
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <span data-attachment-id={value._id} className="inline-flex h-14 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
              <AttachmentThumb attachmentId={value._id} alt="Captured signature" sizeClassName="w-full h-full" />
            </span>
            <span role="img" aria-label="Signature captured" className="text-sm font-medium text-slate-800">
              ✓ Signature captured
            </span>
            {!disabledByPolicy && (
              <button type="button" onClick={() => { setCaptureError(null); onChange(null); }} className="text-red-500 text-xs">
                Clear
              </button>
            )}
          </div>
        ) : (
          <button
            id={field.id}
            type="button"
            data-action="capture-signature"
            disabled={disabledByPolicy}
            onClick={capture}
            aria-required={!!(field.required || resolved?.required)}
            className="px-3 py-2 text-sm border border-dashed border-slate-300 rounded-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Capture signature
          </button>
        )}
        {captureError && (
          <p className="text-xs text-red-600 mt-1">{captureError}</p>
        )}
      </div>
    </FieldWrapper>
  );
}
